import React, { useState, useMemo } from 'react';
import { CheckSquare, XSquare, Sliders, Users, Zap } from 'lucide-react';

const individualServices = [
    { name: 'CRM with unlimited storage and P&L reporting', price: 19.99 },
    { name: 'Project Management', price: 19.99 },
    { name: 'Notes Management', price: 20.00 },
    { name: 'Chat and Collaboration Tool', price: 24.99 },
    { name: 'A.I Reporting', price: 49.99 },
    { name: 'A.I Chat Assistant', price: 24.99 },
    { name: 'A.I meeting assistant for all meeting platforms', price: 49.99 },
];

const PricingCalculator = () => {
    const [selectedServices, setSelectedServices] = useState(individualServices.map(s => s.name));
    const [teamSize, setTeamSize] = useState(1);
    const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'

    const toggleService = (serviceName) => {
        setSelectedServices(prev =>
            prev.includes(serviceName)
                ? prev.filter(name => name !== serviceName)
                : [...prev, serviceName]
        );
    };

    const oldWayCost = useMemo(() => {
        const monthlyCost = individualServices.reduce((total, service) => {
            if (selectedServices.includes(service.name)) {
                return total + service.price * teamSize;
            }
            return total;
        }, 0);
        return billingCycle === 'yearly' ? monthlyCost * 12 : monthlyCost;
    }, [selectedServices, teamSize, billingCycle]);

    const { ourPrice, tierName, savings } = useMemo(() => {
        let monthlyPrice;
        let tier;
        if (teamSize <= 1) {
            monthlyPrice = 0;
            tier = 'Founder Edition';
        } else if (teamSize <= 4) {
            monthlyPrice = 39.99;
            tier = 'Starter';
        } else {
            monthlyPrice = 79.99;
            tier = 'Pro';
        }
        
        const price = billingCycle === 'yearly' ? monthlyPrice * 12 : monthlyPrice;
        
        return { ourPrice: price, tierName: tier, savings: oldWayCost - price };
    }, [oldWayCost, teamSize, billingCycle]);


    const handleTeamSizeChange = (e) => {
        const value = parseInt(e.target.value, 10);
        setTeamSize(value);
    };

    return (
        <section id="calculator" className="py-24 bg-slate-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-bold text-white mb-4">Visualize Your Savings</h2>
                    <p className="text-xl text-slate-400">Stop overpaying for scattered tools. See how much you can save.</p>
                </div>
                
                <div className="flex justify-center items-center gap-4 mb-8">
                    <span className={`font-semibold transition-colors ${billingCycle === 'monthly' ? 'text-sky-400' : 'text-slate-400'}`}>Monthly</span>
                    <div
                        onClick={() => setBillingCycle(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                        className="w-14 h-8 flex items-center bg-slate-700 rounded-full p-1 cursor-pointer"
                    >
                        <div className={`bg-white w-6 h-6 rounded-full shadow-md transform transition-transform ${billingCycle === 'yearly' ? 'translate-x-6' : ''}`}></div>
                    </div>
                    <span className={`font-semibold transition-colors ${billingCycle === 'yearly' ? 'text-sky-400' : 'text-slate-400'}`}>Yearly</span>
                </div>

                <div className="bg-slate-950/50 border border-slate-800 rounded-3xl p-8 lg:p-12 shadow-2xl">
                    <div className="grid lg:grid-cols-2 gap-12">
                        {/* Left side: Service selection */}
                        <div>
                            <h3 className="text-2xl font-bold text-white mb-6">Your Current Tech Stack</h3>
                            <div className="space-y-4">
                                {individualServices.map(service => (
                                    <div
                                        key={service.name}
                                        onClick={() => toggleService(service.name)}
                                        className="flex items-center justify-between p-4 rounded-lg bg-slate-800/60 border border-slate-700 cursor-pointer hover:border-sky-500 transition-all"
                                    >
                                        <div className="flex items-center gap-4">
                                            {selectedServices.includes(service.name) ? (
                                                <CheckSquare className="w-6 h-6 text-sky-400" />
                                            ) : (
                                                <XSquare className="w-6 h-6 text-slate-500" />
                                            )}
                                            <span className="font-medium text-slate-300">{service.name}</span>
                                        </div>
                                        <span className="text-slate-400 font-mono">${service.price.toFixed(2)}/mo</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-8">
                                <label htmlFor="teamSize" className="flex items-center gap-3 text-xl font-bold text-white mb-4">
                                    <Users className="w-6 h-6 text-sky-400" />
                                    <span>Team Size</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <input
                                        id="teamSize"
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={teamSize}
                                        onChange={handleTeamSizeChange}
                                        className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer range-thumb"
                                    />
                                    <span className="text-white font-bold text-2xl w-12 text-center">{teamSize}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right side: Cost comparison */}
                        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 flex flex-col items-center justify-center text-center">
                             <h3 className="text-2xl font-bold text-white mb-4">Your Estimated Costs</h3>
                            <div className="w-full">
                                <div className="mb-6">
                                    <p className="text-slate-400 text-lg">"The Old Way" {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Cost</p>
                                    <p className="text-5xl font-bold text-red-500 line-through decoration-2">
                                        ${oldWayCost.toFixed(2)}
                                    </p>
                                </div>

                                <div className="mb-8 p-6 bg-slate-900/50 rounded-xl border border-slate-600">
                                    <p className="text-slate-300 text-lg">CloudMortgage.io <span className="font-bold text-sky-400">{tierName}</span> Plan</p>
                                    <p className="text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-sky-400 my-2">
                                        ${ourPrice.toFixed(2)}
                                    </p>
                                    <p className="text-slate-400 text-sm">
                                        {
                                            teamSize === 1 ? 'for you.' :
                                            teamSize <= 4 ? `for your team of ${teamSize}. Includes 1 pro seat and ${teamSize-1} free seats.` :
                                            `for your team of ${teamSize}. Includes 1 pro seat and ${teamSize-1} free seats.`
                                        }
                                    </p>
                                </div>
                                
                                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-6">
                                    <p className="text-green-400 text-lg font-semibold">Your {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Savings</p>
                                    <p className="text-5xl font-bold text-green-400">
                                        ${savings > 0 ? savings.toFixed(2) : '0.00'}
                                    </p>
                                    <p className="text-green-300/80 mt-2">Plus, get access to accelerated A.I. reporting and a fully integrated platform.</p>
                                </div>

                                <div className="mt-8 text-slate-300 text-lg">
                                  It's a no-brainer. Stop juggling tools and start closing loans.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default PricingCalculator;
